import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Spinner,
} from "@fluentui/react-components";
import React, { useEffect, useRef } from "react";
import ASCButton from "../Button";

interface DeleteSummary {
  activeOnPortal: boolean;
  casesCount: number;
  commissionsCount: number;
  notesCount: number;
  documentsCount: number;
  hasPartner?: boolean;
  partnerName?: string | null;
  blocked: boolean;
  blockedMessage?: string;
}

interface DeleteClientSummaryPopupProps {
  open: boolean;
  whoText: string;
  summary: DeleteSummary | null;
  summaryLoading: boolean;
  deleteApiProcessing: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

function DeleteClientSummaryPopup(props: DeleteClientSummaryPopupProps) {
  const noButtonRef = useRef<HTMLButtonElement>(null);

  // Default focus must land on "No"/"OK" — a keyboard user pressing Enter
  // without deliberately tabbing to a button must not trigger the delete.
  useEffect(() => {
    if (props.open && !props.summaryLoading) {
      noButtonRef.current?.focus();
    }
  }, [props.open, props.summaryLoading]);

  const blocked = !!props.summary?.blocked;

  return (
    <Dialog open={props.open}>
      <DialogSurface style={{ maxWidth: 540 }}>
        <DialogBody>
          <DialogTitle>
            {blocked ? "Cannot Delete Client" : "Delete Client"}

          </DialogTitle>
          <DialogContent>
            {props.summaryLoading ? (
              <Spinner size="small" label="Loading summary..." />
            ) : blocked ? (
              <p style={{ color: "#5a6a85", lineHeight: 1.5 }}>
                {props.summary?.blockedMessage ||
                  "There are active commissions associated with this client so it cannot be deleted."}
              </p>
            ) : (
              <>
                <p
                  style={{
                    margin: "5px 0",
                  }}
                >
                  <b>Are you sure you want to delete {props.whoText}?</b>
                  <br />
                  All data associated with this client record will be deleted.
                  This action cannot be undone.
                </p>
                <div
                  style={{
                    background: "#f9f9f9",
                    borderRadius: 4,
                    padding: "10px 14px",
                    // marginBottom: 12,
                  }}
                >
                  <div
                    style={{
                      fontWeight: 600,
                      color: "#1a202c",
                      marginBottom: 6,
                    }}
                  >
                    The following will be affected when you delete{" "}
                    {props.whoText}:
                  </div>
                  <ul
                    style={{
                      margin: 0,
                      paddingLeft: 16,
                      lineHeight: 1.65,
                    }}
                  >
                    <li>
                      Client Portal:{" "}
                      {props.summary?.activeOnPortal
                        ? "Active (will be deactivated)"
                        : "Inactive"}
                    </li>
                    <li>Cases associated: {props.summary?.casesCount ?? 0}</li>
                    <li>
                      Commissions: {props.summary?.commissionsCount ?? 0}
                    </li>
                    {props.summary?.hasPartner && (
                      <li>
                        Partner:{" "}
                        {props.summary?.partnerName || "associated partner"}{" "}
                        (will also be deleted)
                      </li>
                    )}
                    <li>Notes: {props.summary?.notesCount ?? 0}</li>
                    <li>Documents: {props.summary?.documentsCount ?? 0}</li>
                  </ul>
                </div>
              </>
            )}
          </DialogContent>
          <DialogActions>
            <ASCButton
              ref={noButtonRef}
              shape="rounded"
              className="asc-button-primary"
              appearance="primary"
              onClick={props.onCancel}
              disabled={!blocked && props.deleteApiProcessing}
              label={blocked ? "OK" : "No"}
            />
            {!blocked && (
              <ASCButton
                shape="rounded"
                appearance="secondary"
                onClick={() => props.onConfirm()}
                disabled={props.summaryLoading || props.deleteApiProcessing}
                icon={props.deleteApiProcessing ? <Spinner size="tiny" /> : null}
                label={props.deleteApiProcessing ? "Processing..." : "Yes, Delete"}
                style={{ color: "#c0392b", borderColor: "#e3b3b3" }}
              />
            )}
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

export default DeleteClientSummaryPopup;
